/**
 * useScreenZoom — per-screen zoom via Ctrl/Cmd + Plus / Minus / 0.
 *
 * Browser zoom (Ctrl +/-) is per-DOMAIN, so zooming the LED overlay also
 * resized the admin panel and every other tab of the app. This hook
 * intercepts those keys, blocks the browser zoom, and instead applies a CSS
 * zoom to #root for the CURRENT route only — persisted per path in
 * localStorage, so each screen (LED, Stream, admin panel, live auction…)
 * remembers its own zoom level independently.
 *
 * Zoom is applied to #root (not <body>) on purpose: the player-card PNG
 * export appends its off-screen html2canvas clone to document.body, which
 * stays unaffected and always rasterises at true size.
 */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";

const MIN = 0.5;
const MAX = 2.0;
const STEP = 0.1;

const keyFor = (path) => `screen-zoom:${path}`;
const readZoom = (path) => {
  const v = parseFloat(localStorage.getItem(keyFor(path)) || "1");
  return Number.isFinite(v) ? Math.min(MAX, Math.max(MIN, v)) : 1;
};
const applyZoom = (level) => {
  const root = document.getElementById("root");
  if (!root) return;
  root.style.zoom = level === 1 ? "" : String(level);
  if (level === 1) {
    root.style.minHeight = "";
    for (const el of [document.documentElement, document.body]) {
      el.style.backgroundColor = "";
      el.style.backgroundImage = "";
    }
    return;
  }
  // Counteract visual shrink so the page still fills the viewport, and mirror
  // the page's own background onto <body> so no bare band shows below it.
  root.style.minHeight = `${Math.round(10000 / level) / 100}vh`;
  const paints = (el) => {
    if (!el) return false;
    const cs = getComputedStyle(el);
    return cs.backgroundImage !== "none" || (cs.backgroundColor !== "rgba(0, 0, 0, 0)" && cs.backgroundColor !== "transparent");
  };
  // Find the first element (depth ≤ 2) that actually paints a background —
  // the page container isn't always root's first child (e.g. toaster nodes).
  let bgEl = null;
  for (const child of root.children) {
    if (paints(child)) { bgEl = child; break; }
    for (const g of child.children || []) {
      if (paints(g)) { bgEl = g; break; }
    }
    if (bgEl) break;
  }
  if (bgEl) {
    const cs = getComputedStyle(bgEl);
    // html carries its own background in index.css, which is what actually
    // paints the canvas below the zoomed root — so set BOTH html and body.
    // Also derive a SOLID fallback (last gradient stop) because a gradient
    // sized to a shorter document can leave the canvas uncovered.
    let solid = cs.backgroundColor;
    if ((!solid || solid === "rgba(0, 0, 0, 0)" || solid === "transparent") && cs.backgroundImage !== "none") {
      const stops = cs.backgroundImage.match(/rgba?\([^)]+\)|#[0-9a-fA-F]{3,8}/g);
      if (stops && stops.length) solid = stops[stops.length - 1];
    }
    for (const el of [document.documentElement, document.body]) {
      el.style.backgroundImage = cs.backgroundImage;
      if (solid) el.style.backgroundColor = solid;
    }
  }
};

export function useScreenZoom() {
  const { pathname } = useLocation();

  // Reapply this screen's stored zoom whenever the route changes.
  useEffect(() => {
    applyZoom(readZoom(pathname));
  }, [pathname]);

  useEffect(() => {
    const onKey = (e) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      const zoomIn = e.key === "+" || e.key === "=" || e.code === "Equal" || e.code === "NumpadAdd";
      const zoomOut = e.key === "-" || e.key === "_" || e.code === "Minus" || e.code === "NumpadSubtract";
      const reset = e.key === "0" || e.code === "Digit0" || e.code === "Numpad0";
      if (!zoomIn && !zoomOut && !reset) return;
      e.preventDefault();
      const path = window.location.pathname;
      let level = readZoom(path);
      if (zoomIn) level = Math.min(MAX, Math.round((level + STEP) * 100) / 100);
      else if (zoomOut) level = Math.max(MIN, Math.round((level - STEP) * 100) / 100);
      else level = 1;
      localStorage.setItem(keyFor(path), String(level));
      applyZoom(level);
      toast(`Zoom ${Math.round(level * 100)}% — this screen only`, { id: "screen-zoom", duration: 1200 });
    };
    window.addEventListener("keydown", onKey, { capture: true });
    return () => window.removeEventListener("keydown", onKey, { capture: true });
  }, []);
}
